import React, { useState, useCallback, useRef } from 'react';
import { Box, IconButton, Tooltip } from '@mui/material';
import { Delete, DragIndicator, FileCopy, KeyboardArrowUp, KeyboardArrowDown } from '@mui/icons-material';

// Импортируем компоненты блоков
import TextBlock from './TextBlock';
import HeadingBlock from './HeadingBlock';
import ImageBlock from './ImageBlock';
import ButtonBlock from './ButtonBlock';
import DividerBlock from './DividerBlock';
import SpacerBlock from './SpacerBlock';
import ListBlock from './ListBlock';
import TestimonialBlock from './TestimonialBlock';
import ProductBlock from './ProductBlock';
import EventBlock from './EventBlock';
import SocialIconsBlock from './SocialIconsBlock';
import FooterBlock from './FooterBlock';
import UnsubscribeBlock from './UnsubscribeBlock';
import ContactUsBlock from './ContactUsBlock';
import HeroBlock from './HeroBlock';

// Импортируем утилитарные функции
import { duplicateBlockWithUniqueIds } from '../../utils/blockUtils';

const ContainerBlock = ({ data, isSelected, onUpdate, previewMode, onAddToColumn, onSelectBlock, onUpdateBlock, onDeleteBlock, blockId }) => {
  const [dragOverColumn, setDragOverColumn] = useState(null);
  
  // Throttling для drag events
  const dragThrottleRef = useRef(null);
  const lastDragOverColumn = useRef(null);
  
  // Динамический импорт для избежания циклических зависимостей
  const getBlockComponent = useCallback((blockType) => {
    const basicComponents = {
      text: TextBlock,
      heading: HeadingBlock,
      image: ImageBlock,
      button: ButtonBlock,
      divider: DividerBlock,
      spacer: SpacerBlock,
      list: ListBlock,
      testimonial: TestimonialBlock,
      product: ProductBlock,
      event: EventBlock,
      'social-icons': SocialIconsBlock,
      footer: FooterBlock,
      unsubscribe: UnsubscribeBlock,
      contactUs: ContactUsBlock,
      hero: HeroBlock,
    };
    
    // Для columns и container используем lazy loading
    if (blockType === 'columns' || blockType === 'columns-3') {
      const ColumnsBlock = require('./ColumnsBlock').default;
      return ColumnsBlock;
    }
    
    if (blockType === 'container') {
      return ContainerBlock;
    }
    
    return basicComponents[blockType];
  }, []);
  
  const {
    backgroundColor = 'transparent',
    padding = 10,
    borderRadius = 0,
    maxWidth = '100%',
    content = [], // Single column content
    marginTop = 15,
    marginBottom = 15,
    verticalAlign = 'top',
    opacity = 1,
    boxShadow = 'none',
    backgroundImage = '',
    backgroundSize = 'cover',
    backgroundPosition = 'center',
  } = data;

  const containerStyles = {
    marginTop: `${marginTop}px`,
    marginBottom: `${marginBottom}px`,
    padding: `${padding}px`,
    border: isSelected ? '1px dashed #007bff' : 'none',
    borderRadius: isSelected ? '4px' : `${borderRadius}px`,
    backgroundColor,
    backgroundImage: backgroundImage ? `url(${backgroundImage})` : 'none',
    backgroundSize,
    backgroundPosition,
    backgroundRepeat: 'no-repeat',
    opacity,
    boxShadow: boxShadow !== 'none' ? boxShadow : 'none',
    maxWidth,
    margin: '0 auto',
  };

  // Оптимизированные обработчики drag-and-drop (как в ColumnsBlock)
  const handleColumnDragOver = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Throttling - обновляем индекс только если он изменился
    if (lastDragOverColumn.current !== 0) {
      if (dragThrottleRef.current) {
        clearTimeout(dragThrottleRef.current);
      }
      
      dragThrottleRef.current = setTimeout(() => {
        setDragOverColumn(0);
        lastDragOverColumn.current = 0;
      }, 16); // ~60fps
    }
  }, []);

  const handleColumnDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Проверяем, действительно ли мышь покинула контейнер
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX;
    const y = e.clientY;
    
    if (x < rect.left || x > rect.right || y < rect.top || y > rect.bottom) {
      if (dragThrottleRef.current) {
        clearTimeout(dragThrottleRef.current);
      }
      
      dragThrottleRef.current = setTimeout(() => {
        setDragOverColumn(null);
        lastDragOverColumn.current = null;
      }, 16);
    }
  }, []);

  const handleColumnDrop = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    
    // Очищаем throttling
    if (dragThrottleRef.current) {
      clearTimeout(dragThrottleRef.current);
      dragThrottleRef.current = null;
    }
    
    setDragOverColumn(null);
    lastDragOverColumn.current = null;
    
    try {
      const blockData = JSON.parse(e.dataTransfer.getData('application/json'));
      if (blockData && blockData.type && onAddToColumn) {
        // Используем данные блока из canvas или defaultData для новых блоков
        const dataToUse = blockData.fromCanvas ? blockData.data : blockData.defaultData;
        const fromCanvasId = blockData.fromCanvas ? blockData.originalId : null;
        
        onAddToColumn(0, blockData.type, dataToUse, fromCanvasId);
        
        // Блок успешно добавлен в контейнер
      }
    } catch (error) {
      console.error('Error parsing dropped data:', error);
    }
  }, [onAddToColumn]);

  const renderColumnContent = () => {
    const columnContent = content || [];
    const isDropZone = dragOverColumn === 0;

    if (!columnContent || columnContent.length === 0) {
      return (
        <Box
          sx={{
            minHeight: '80px',
            backgroundColor: isDropZone ? '#e3f2fd' : '#f8f9fa',
            border: isDropZone ? '2px solid #2196f3' : '2px dashed #dee2e6',
            borderRadius: '4px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: isDropZone ? '#1976d2' : '#6c757d',
            fontSize: '14px',
            textAlign: 'center',
            padding: '20px',
            transition: 'all 0.2s ease',
            willChange: 'background-color, border-color, color',
            backfaceVisibility: 'hidden',
            transform: 'translateZ(0)',
          }}
          onDragOver={handleColumnDragOver}
          onDragLeave={handleColumnDragLeave}
          onDrop={handleColumnDrop}
        >
          {isDropZone ? 'Drop block here' : 'Drop content here'}
        </Box>
      );
    }

    // Рендерим полноценные компоненты блоков
    return (
      <Box 
        sx={{ 
          minHeight: '50px', 
          border: isDropZone ? '2px solid #2196f3' : 'none',
          borderRadius: '4px',
          transition: 'border-color 0.2s ease',
          willChange: isDropZone ? 'border-color' : 'auto',
          backfaceVisibility: 'hidden',
          transform: 'translateZ(0)',
        }}
        onDragOver={handleColumnDragOver}
        onDragLeave={handleColumnDragLeave}
        onDrop={handleColumnDrop}
      >
        {columnContent.map((item, index) => {
          const BlockComponent = getBlockComponent(item.type);
          
          if (!BlockComponent) {
            console.warn(`Unknown block type in container: ${item.type}`, item);
            return null;
          }

          return (
            <Box
              key={item.id || index}
              sx={{
                position: 'relative',
                mb: 1,
                border: '1px solid transparent',
                borderRadius: '4px',
                cursor: 'pointer',
                '&:hover': {
                  border: '1px solid #007bff',
                  '& .column-block-controls': {
                    opacity: 1,
                  },
                  '& .drag-handle': {
                    opacity: 1,
                  },
                },
              }}
              onClick={(e) => {
                // Не блокируем событие полностью, только если нужно выбрать внутренний блок
                if (onSelectBlock) {
                  e.stopPropagation();
                  // Передаем полную информацию о родителе для правильного обновления
                  onSelectBlock({
                    ...item,
                    parentId: blockId,
                    parentType: 'container',
                    columnIndex: 0,
                    parentIndex: index
                  });
                }
              }}
            >
              {/* Мини панель управления для блока в контейнере */}
              <Box
                className="column-block-controls"
                sx={{
                  position: 'absolute',
                  top: -25,
                  right: 0,
                  height: 20,
                  backgroundColor: '#2c3e50',
                  borderRadius: '4px 4px 0 0',
                  display: 'flex',
                  alignItems: 'center',
                  px: 0.5,
                  opacity: 0,
                  transition: 'opacity 0.2s',
                  zIndex: 100,
                  pointerEvents: 'auto',
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <Tooltip title="Move Up">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (index === 0) return; // Уже наверху
                      
                      const updatedData = { ...data };
                      const newContent = [...content];
                      [newContent[index], newContent[index - 1]] = [newContent[index - 1], newContent[index]];
                      updatedData.content = newContent;
                      onUpdate(updatedData);
                    }}
                    sx={{ color: '#9b59b6', padding: 0.25, fontSize: '12px' }}
                    disabled={index === 0}
                  >
                    <KeyboardArrowUp fontSize="inherit" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Move Down">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      if (index === content.length - 1) return; // Уже внизу
                      
                      const updatedData = { ...data };
                      const newContent = [...content];
                      [newContent[index], newContent[index + 1]] = [newContent[index + 1], newContent[index]];
                      updatedData.content = newContent;
                      onUpdate(updatedData);
                    }}
                    sx={{ color: '#9b59b6', padding: 0.25, fontSize: '12px' }}
                    disabled={index === content.length - 1}
                  >
                    <KeyboardArrowDown fontSize="inherit" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Duplicate">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      const duplicatedItem = duplicateBlockWithUniqueIds(item);
                      const updatedData = { ...data };
                      const newContent = [...content];
                      newContent.splice(index + 1, 0, duplicatedItem);
                      updatedData.content = newContent;
                      onUpdate(updatedData);
                    }}
                    sx={{ color: '#3498db', padding: 0.25, fontSize: '12px' }}
                  >
                    <FileCopy fontSize="inherit" />
                  </IconButton>
                </Tooltip>

                <Tooltip title="Delete">
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      const updatedData = { ...data };
                      const newContent = content.filter((_, i) => i !== index);
                      updatedData.content = newContent;
                      onUpdate(updatedData);
                    }}
                    sx={{ color: '#dc3545', padding: 0.25, fontSize: '12px' }}
                  >
                    <Delete fontSize="inherit" />
                  </IconButton>
                </Tooltip>
              </Box>

              <Box
                sx={{ 
                  position: 'relative',
                  '&:hover .drag-handle': {
                    opacity: 1,
                  }
                }}
              >
                <Box
                  className="drag-handle"
                  sx={{
                    position: 'absolute',
                    left: -20,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    cursor: 'grab',
                    opacity: 0,
                    transition: 'opacity 0.2s',
                    zIndex: 50,
                    backgroundColor: '#007bff',
                    borderRadius: '50%',
                    width: 16,
                    height: 16,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  draggable
                  onDragStart={(e) => {
                    e.stopPropagation();
                    
                    // Создаем данные для перетаскивания элемента из контейнера
                    const dragData = {
                      type: item.type,
                      data: item.data,
                      defaultData: item.data,
                      fromContainer: true,
                      sourceColumnIndex: 0,
                      sourceItemIndex: index,
                      sourceBlockId: blockId,
                      originalId: item.id
                    };
                    
                    e.dataTransfer.setData('application/json', JSON.stringify(dragData));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  onDragEnd={(e) => {
                    e.stopPropagation();
                  }}
                >
                  <DragIndicator sx={{ fontSize: 12, color: 'white' }} />
                </Box>

                <BlockComponent
                  data={item.data}
                  isSelected={false}
                  onUpdate={(updatedData) => {
                    const updatedContent = [...content];
                    updatedContent[index] = { ...item, data: { ...item.data, ...updatedData } };
                    onUpdate({ content: updatedContent });
                  }}
                  previewMode={previewMode}
                  blockId={item.id}
                  onSelectBlock={onSelectBlock}
                  onUpdateBlock={(nestedBlockId, nestedUpdateData) => {
                    console.log('Updating nested block in container:', { nestedBlockId, nestedUpdateData });
                    
                    // Если это обновление текущего элемента
                    if (nestedBlockId === item.id) {
                      const updatedContent = [...content];
                      updatedContent[index] = { 
                        ...item, 
                        data: typeof nestedUpdateData === 'function' 
                          ? nestedUpdateData(item.data) 
                          : { ...item.data, ...nestedUpdateData }
                      };
                      onUpdate({ content: updatedContent });
                    } else {
                      // Передаем обновление дальше по цепочке
                      if (onUpdateBlock) {
                        onUpdateBlock(nestedBlockId, nestedUpdateData);
                      }
                    }
                  }}
                  onDeleteBlock={onDeleteBlock}
                  onAddToColumn={(item.type === 'columns' || item.type === 'columns-3' || item.type === 'container') ? 
                    (nestedColumnIndex, nestedBlockType, nestedBlockData, nestedFromCanvasId = null) => {
                      console.log('Adding to nested container item:', { nestedColumnIndex, nestedBlockType, nestedFromCanvasId });
                      
                      // Создаем новый блок для вложенной колонки/контейнера
                      const newNestedBlock = {
                        id: Date.now().toString() + '_nested_' + nestedColumnIndex,
                        type: nestedBlockType,
                        data: nestedBlockData || {},
                        position: 0,
                      };
                      
                      // Обновляем вложенный блок
                      const currentItem = { ...item };
                      
                      if (currentItem.type === 'columns' || currentItem.type === 'columns-3') {
                        // Обновляем вложенные колонки
                        if (currentItem.data.columns === 2) {
                          if (nestedColumnIndex === 0) {
                            currentItem.data.leftContent = [...(currentItem.data.leftContent || []), newNestedBlock];
                          } else {
                            currentItem.data.rightContent = [...(currentItem.data.rightContent || []), newNestedBlock];
                          }
                        } else {
                          currentItem.data.content = currentItem.data.content || [[], [], []];
                          currentItem.data.content[nestedColumnIndex] = [...(currentItem.data.content[nestedColumnIndex] || []), newNestedBlock];
                        }
                      } else if (currentItem.type === 'container') {
                        // Обновляем вложенный контейнер
                        const exists = (currentItem.data.content || []).some(b => b.id === newNestedBlock.id);
if (!exists) {
  currentItem.data.content = [...(currentItem.data.content || []), newNestedBlock];
}
                      }
                      
                      // Обновляем родительский контейнер
                      const updatedContent = [...content];
                      updatedContent[index] = currentItem;
                      onUpdate({ content: updatedContent });
                      
                      // Удаляем исходный блок если он был перемещен с canvas
                      if (nestedFromCanvasId && onDeleteBlock) {
                        onDeleteBlock(nestedFromCanvasId);
                      }
                    } : undefined
                  }
                />
              </Box>
            </Box>
          );
        })}
      </Box>
    );
  };

  return (
    <Box
      sx={containerStyles}
    >
      <Box sx={{ display: 'flex', alignItems: verticalAlign === 'top' ? 'flex-start' : verticalAlign === 'bottom' ? 'flex-end' : 'center' }}>
        <Box sx={{ width: '100%' }}>
          {renderColumnContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default ContainerBlock; 